import { checklistFor, pendingChecklist } from '../models/claim-forms';
import { Endorsement, EndorsementType, Policy, PolicyDocument, ProtoDb } from '../models/domain.models';
import { CLAIM_STATUS_LABELS } from '../models/labels';
import { initialVersion, LINE_PREFIX } from './policy-helpers';
import { addDays } from './rng';

const THIRD_NAMES = ['Julián Peralta', 'Mariela Costa', 'Rubén Aguilar', 'Silvia Barrios'];
const THIRD_INSURERS = ['Seguros del Litoral', 'Nexo Seguros', 'Sin seguro', 'La Austral Seguros'];
const PLACES = ['Av. Rivadavia 1200, CABA', 'Ruta 8 km 52', 'Belgrano 340, Rosario', 'Mitre 980, La Plata'];
const SEED_ENDORSEMENTS: EndorsementType[] = ['coverage-change', 'data-change', 'item-add', 'free'];
const SEED_DELTAS = [18000, 0, 9500, 0];
const OPEN_CLAIM = ['reported', 'documentation', 'in-analysis'];

function pad(n: number, size: number): string {
  return String(n).padStart(size, '0');
}

function earliest(a: string, b: string): string {
  return a < b ? a : b;
}

/** Lifecycle data derived without the RNG, so the base seed stays identical to the one the earlier passes built. */
export function enrichLifecycle(db: ProtoDb, today: string, idp = ''): void {
  const company = (oid: string) => db.companies.find(c => c.oid === oid)!;
  const yesterday = addDays(today, -1);

  db.claims.forEach((claim, i) => {
    const caps = company(claim.companyOid).capabilities;
    const open = OPEN_CLAIM.includes(claim.status);
    const total = checklistFor(claim.type).length;
    claim.route = caps.reportClaim === 'api' ? 'api' : 'manual';
    claim.incidentTime = `${pad(8 + (i % 12), 2)}:30`;
    claim.location = PLACES[i % PLACES.length];
    claim.injured = claim.type === 'Choque' && i % 4 === 0;
    claim.policeReport = /Robo|Choque|Incendio/.test(claim.type);
    claim.checklist = pendingChecklist(claim.type).map((item, n) => {
      const received = !open || n < i % (total + 1);
      return { ...item, received, receivedDate: received ? addDays(claim.reportedDate, 1) : null };
    });
    if (claim.type === 'Choque' || claim.type.startsWith('Responsabilidad')) {
      claim.thirdParties = [{
        name: THIRD_NAMES[i % THIRD_NAMES.length],
        documentNumber: String(20000000 + ((i * 7919) % 9000000)),
        phone: `11 4${pad(100 + i, 3)}-${pad((i * 37) % 10000, 4)}`,
        vehicle: 'Vehículo de terceros',
        plate: `AC${pad(100 + i * 11, 3)}ZB`,
        insurer: THIRD_INSURERS[i % THIRD_INSURERS.length],
      }];
    }
    claim.history = [{
      date: claim.reportedDate, status: 'reported', text: 'Denuncia registrada', origin: claim.route === 'api' ? 'company' : 'manual',
    }];
    if (claim.status !== 'reported') {
      claim.history.push({
        date: claim.lastUpdateDate, status: claim.status, text: `Estado: ${CLAIM_STATUS_LABELS[claim.status]}`,
        origin: caps.queryClaims === 'api' ? 'company' : 'manual',
      });
    }
    claim.lastQueryDate = open && caps.queryClaims === 'api' ? yesterday : null;
  });

  db.persons.forEach((person, i) => {
    if (i % 17 === 5) person.commercialOptOut = true;
  });

  const overduePolicies = [...new Set(db.installments.filter(i => i.status === 'overdue').map(i => i.policyOid))];
  overduePolicies
    .map(oid => db.policies.find(p => p.oid === oid)!)
    .filter(p => company(p.companyOid).capabilities.queryPolicies === 'api')
    .slice(0, 2)
    .forEach(p => {
      const due = db.installments.find(i => i.policyOid === p.oid && i.status === 'overdue')!.dueDate;
      p.suspension = { date: earliest(addDays(due, 30), today) };
    });

  for (const policy of db.policies.filter(p => p.status === 'cancelled')) {
    policy.versions.push({
      version: 2, effectDate: earliest(addDays(policy.startDate, 90), yesterday), endorsementOid: null,
      premium: policy.premium, subject: policy.subject, status: 'cancelled', endDate: policy.endDate,
    });
  }

  const addDocument = (name: string, origin: PolicyDocument['origin'], date: string, policy: Policy): string => {
    const oid = `doc-${idp}${pad(db.documents.length + 1, 4)}`;
    db.documents.push({
      oid, name, kind: 'endorsement', origin, date, ownerOid: policy.ownerOid, personOid: policy.personOid,
      policyOid: policy.oid, claimOid: null, uploadedByOid: origin === 'uploaded' ? policy.ownerOid : null, links: [],
    });
    return oid;
  };
  let sequence = 0;
  const addEndorsement = (policy: Policy, fields: Partial<Endorsement> & Pick<Endorsement, 'type' | 'route' | 'status'>): Endorsement => {
    sequence++;
    const endorsement: Endorsement = {
      oid: `end-${idp}${pad(sequence, 4)}`, number: `END-${idp.slice(0, 1).toUpperCase()}${pad(sequence, 4)}`, policyOid: policy.oid, ownerOid: policy.ownerOid,
      origin: fields.route === 'api' ? 'platform' : 'manual', effectDate: today, issueDate: null, registerDate: today,
      companyNumber: null, premiumDelta: null, description: '', details: {}, documentOid: null, ticketOid: null,
      resultingVersion: null, rejectionMessage: null, ...fields,
    };
    db.endorsements.push(endorsement);
    return endorsement;
  };

  const pendingKinds = ['coverage-change', 'data-change', 'item-add'] as const;
  db.tickets
    .filter(t => (t.kind === 'endorsement' || t.kind === 'cancellation') && t.relatedType === 'policy')
    .forEach((ticket, n) => {
      const policy = db.policies.find(p => p.oid === ticket.relatedOid);
      if (!policy || company(policy.companyOid).capabilities.endorse !== 'api') return;
      addEndorsement(policy, {
        type: ticket.kind === 'cancellation' ? 'cancellation' : pendingKinds[n % pendingKinds.length],
        route: 'api', status: 'sent', registerDate: addDays(today, -2), ticketOid: ticket.oid,
        description: 'Solicitud enviada a la compañía, a la espera de su confirmación.',
      });
    });

  db.policies.forEach((policy, i) => {
    if (policy.status !== 'active' || i % 23 !== 4) return;
    const k = sequence;
    const type = SEED_ENDORSEMENTS[k % SEED_ENDORSEMENTS.length];
    const route = type === 'free' ? 'free' : company(policy.companyOid).capabilities.endorse === 'api' ? 'api' : 'manual';
    const effectDate = earliest(addDays(policy.startDate, 20 + (k % 5) * 7), yesterday);
    const registerDate = k % 3 === 0 ? earliest(addDays(effectDate, 6), today) : effectDate;
    const delta = type === 'free' || type === 'data-change' ? 0 : SEED_DELTAS[k % SEED_DELTAS.length];
    const endorsement = addEndorsement(policy, {
      type, route, status: route === 'api' ? 'confirmed' : 'registered', effectDate, registerDate,
      issueDate: route === 'free' ? null : addDays(effectDate, 1),
      companyNumber: route === 'free' ? null : `CE-${pad(70000 + k * 13, 5)}`,
      premiumDelta: route === 'free' ? null : delta, resultingVersion: 2,
      description: route === 'free' ? 'Corrección de un dato de contacto del tomador.' : 'Endoso de demostración.',
    });
    endorsement.documentOid = addDocument(
      `Endoso ${endorsement.number}.pdf`, route === 'api' ? 'from-company' : 'uploaded', registerDate, policy);
    policy.premium += delta;
    policy.versions.push({
      version: 2, effectDate, endorsementOid: endorsement.oid, premium: policy.premium, subject: policy.subject,
      status: 'active', endDate: policy.endDate,
    });
  });

  db.policies.filter(p => p.status === 'expired').slice(0, 3).forEach((previous, k) => {
    const next = k === 1
      ? db.companies.find(c => c.oid !== previous.companyOid && c.lines.includes(previous.lineOfBusiness))!
      : company(previous.companyOid);
    const premium = Math.round((previous.premium * 1.12) / 100) * 100;
    db.policies.push({
      ...previous,
      oid: `pol-${idp}${pad(db.policies.length + 1, 4)}`,
      number: `${LINE_PREFIX[previous.lineOfBusiness]}-${pad(900000 + k + (idp ? 100 : 0), 6)}`,
      companyOid: next.oid,
      origin: next.capabilities.queryPolicies === 'api' ? 'api-import' : 'file-import',
      status: 'active',
      startDate: previous.endDate,
      endDate: addDays(previous.endDate, 365),
      premium,
      lastSyncDate: today,
      divergent: false,
      renewalOfOid: previous.oid,
      installmentsLoaded: false,
      versions: [initialVersion(previous.endDate, premium, previous.subject)],
    });
  });
}
